import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Search, 
  Share2, 
  Target, 
  FileText, 
  Mail, 
  Code, 
  BarChart3,
  ArrowRight,
  CheckCircle,
  TrendingUp,
  MousePointer,
  Users,
  PenTool,
  MessageSquare,
  Globe,
  LineChart
} from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    id: "seo",
    icon: Search,
    title: "SEO & Search Marketing",
    description: "Dominate search engine results with our comprehensive SEO strategies. We optimize your online presence to drive organic traffic and improve visibility.",
    features: [
      "Technical SEO audits and optimization",
      "Keyword research and strategy",
      "On-page and off-page SEO",
      "Local SEO optimization",
      "Link building campaigns",
      "SEO performance tracking"
    ],
    benefits: [
      { icon: TrendingUp, text: "Increased organic traffic" },
      { icon: Target, text: "Higher search rankings" },
      { icon: Users, text: "Quality lead generation" }
    ]
  },
  {
    id: "social",
    icon: Share2,
    title: "Social Media Marketing",
    description: "Build meaningful connections with your audience across all major social platforms. We create engaging content that resonates and converts.",
    features: [
      "Social media strategy development",
      "Content creation and curation",
      "Community management",
      "Influencer partnerships",
      "Social listening and monitoring",
      "Performance analytics"
    ],
    benefits: [
      { icon: Users, text: "Engaged community" },
      { icon: MessageSquare, text: "Brand awareness" },
      { icon: TrendingUp, text: "Social conversions" }
    ]
  },
  {
    id: "ppc",
    icon: Target,
    title: "PPC Advertising",
    description: "Maximize your advertising ROI with precision-targeted paid campaigns across Google, Meta, LinkedIn, and more.",
    features: [
      "Google Ads management",
      "Meta (Facebook/Instagram) ads",
      "LinkedIn advertising",
      "Display and remarketing campaigns",
      "Landing page optimization",
      "A/B testing and optimization"
    ],
    benefits: [
      { icon: MousePointer, text: "Immediate visibility" },
      { icon: Target, text: "Precise targeting" },
      { icon: LineChart, text: "Measurable ROI" }
    ]
  },
  {
    id: "content",
    icon: FileText,
    title: "Content Marketing",
    description: "Create compelling content that educates, entertains, and converts. Our content strategies drive engagement and establish thought leadership.",
    features: [
      "Content strategy development",
      "Blog writing and management",
      "Video content production",
      "Infographics and visual content",
      "Ebooks and whitepapers",
      "Content distribution"
    ],
    benefits: [
      { icon: PenTool, text: "Brand authority" },
      { icon: Users, text: "Audience engagement" },
      { icon: Search, text: "SEO benefits" }
    ]
  },
  {
    id: "email",
    icon: Mail,
    title: "Email Marketing",
    description: "Nurture leads and drive conversions with personalized email campaigns. From automation to newsletters, we help you connect with your audience.",
    features: [
      "Email strategy and planning",
      "Campaign design and development",
      "Marketing automation",
      "List segmentation",
      "A/B testing",
      "Performance analytics"
    ],
    benefits: [
      { icon: Target, text: "Targeted messaging" },
      { icon: TrendingUp, text: "High ROI channel" },
      { icon: Users, text: "Customer retention" }
    ]
  },
  {
    id: "web",
    icon: Code,
    title: "Web Development",
    description: "Build fast, responsive, and conversion-optimized websites. We create digital experiences that look great and perform even better.",
    features: [
      "Custom website development",
      "E-commerce solutions",
      "CMS implementation",
      "Website redesign",
      "Performance optimization",
      "Maintenance and support"
    ],
    benefits: [
      { icon: Globe, text: "Professional presence" },
      { icon: TrendingUp, text: "Conversion focused" },
      { icon: Code, text: "Modern technology" }
    ]
  },
  {
    id: "analytics",
    icon: BarChart3,
    title: "Analytics & Reporting",
    description: "Make data-driven decisions with comprehensive analytics and reporting. We turn complex data into actionable insights for your business.",
    features: [
      "Google Analytics setup",
      "Custom dashboard creation",
      "Conversion tracking",
      "Attribution modeling",
      "Regular performance reports",
      "Strategic recommendations"
    ],
    benefits: [
      { icon: LineChart, text: "Data-driven decisions" },
      { icon: Target, text: "Clear insights" },
      { icon: TrendingUp, text: "Continuous improvement" }
    ]
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const Services = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Our Services</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-2 mb-6 leading-tight">
              Comprehensive Digital Marketing Solutions
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              From strategy to execution, we offer end-to-end digital marketing services 
              designed to help your business grow. Each service is tailored to your 
              unique goals and challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-24"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                variants={itemVariants}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={`${index % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="inline-flex items-center gap-3 mb-6">
                    <div className="w-14 h-14 bg-[#4f46e5]/10 rounded-xl flex items-center justify-center">
                      <service.icon className="w-7 h-7 text-[#4f46e5]" />
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{service.title}</h2>
                  </div>
                  
                  <p className="text-slate-600 mb-8 leading-relaxed text-lg">
                    {service.description}
                  </p>

                  {/* Benefits */}
                  <div className="flex flex-wrap gap-4 mb-8">
                    {service.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full">
                        <benefit.icon className="w-4 h-4 text-[#4f46e5]" />
                        <span className="text-sm text-slate-700 font-medium">{benefit.text}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/contact">
                    <Button 
                      data-testid={`service-cta-${service.id}`}
                      className="bg-[#1e3a8a] hover:bg-[#172554] text-white px-6"
                    >
                      Get Started
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>

                <div className={`${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm">
                    <h3 className="text-lg font-semibold text-slate-900 mb-6">What's Included</h3>
                    <ul className="space-y-4">
                      {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-[#4f46e5] flex-shrink-0 mt-0.5" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">
              How We Work
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our proven process ensures consistent results and transparent collaboration 
              throughout your digital marketing journey.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Discovery", description: "We learn about your business, goals, audience, and competitive landscape." },
              { step: "02", title: "Strategy", description: "We develop a customized strategy aligned with your objectives and budget." },
              { step: "03", title: "Execution", description: "Our team implements the strategy with precision and attention to detail." },
              { step: "04", title: "Optimize", description: "We continuously monitor, analyze, and optimize for better results." }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-xl p-6 border border-slate-200 h-full">
                  <span className="text-5xl font-bold text-slate-100">{item.step}</span>
                  <h3 className="text-lg font-semibold text-slate-900 mt-4 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-slate-200"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-[#1e3a8a] rounded-3xl px-8 py-16 sm:px-16 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>
            
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Transform Your Digital Presence?
              </h2>
              <p className="text-blue-100 max-w-2xl mx-auto mb-8">
                Let's discuss how our services can help you achieve your business goals. 
                Schedule a free consultation with our experts today.
              </p>
              <Link to="/contact">
                <Button 
                  data-testid="services-cta-btn"
                  size="lg" 
                  className="bg-white text-[#1e3a8a] hover:bg-slate-100 px-8"
                >
                  Schedule a Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
